import type { CharacterParts, PixelData } from '@vikings/types';

// ═══════════════════════════════════════════════
// Seeded PRNG (mulberry32)
// ═══════════════════════════════════════════════

function mulberry32(seed: number): () => number {
  return () => {
    seed |= 0;
    seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Hash a phone number string to a numeric seed.
 * Uses a simple DJB2 hash.
 */
export function phoneToSeed(phone: string): number {
  let hash = 5381;
  for (let i = 0; i < phone.length; i++) {
    hash = ((hash << 5) + hash + phone.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
}

// ═══════════════════════════════════════════════
// Color palettes
// ═══════════════════════════════════════════════

const SKIN_COLORS = ['#f5d6b8', '#e8b88a', '#d4956b', '#b87346', '#8b5e3c', '#6b4226'];
const HAIR_COLORS = ['#f5e6c8', '#c9a654', '#8b6914', '#5c3d11', '#2c1810', '#a83232', '#e07020'];
const CLOTHING_COLORS = [
  '#4a6fa5', '#6b8e4e', '#9e4f3e', '#7b5ea7', '#c9a654', '#5e8c8a', '#8b4e6a',
  '#b87846', '#4e7c6a', '#6a4e8c',
];
const HELMET_COLORS = ['#8b8b8b', '#a0a0a0', '#c9a654', '#b87846', '#6b5a3e', '#5e5e5e'];

// ═══════════════════════════════════════════════
// Character part definitions
// ═══════════════════════════════════════════════

/**
 * Generate character visual parts from a phone number.
 * Deterministic: same phone always produces same character.
 */
export function generateCharacterParts(phone: string): CharacterParts {
  const seed = phoneToSeed(phone);
  const rng = mulberry32(seed);

  const pick = <T>(arr: T[]): T => arr[Math.floor(rng() * arr.length)]!;
  const pickIndex = (max: number): number => Math.floor(rng() * max);

  return {
    skinColor: pick(SKIN_COLORS),
    hairColor: pick(HAIR_COLORS),
    beardType: pickIndex(4), // 0=none, 1=short, 2=medium, 3=long
    helmetType: pickIndex(4), // 0=basic, 1=horned, 2=winged, 3=crown
    hornType: pickIndex(3), // 0=none, 1=small, 2=large
    noseType: pickIndex(3), // 0=small, 1=medium, 2=large
    clothingType: pickIndex(3), // 0=tunic, 1=armor, 2=fur
    clothingColor: pick(CLOTHING_COLORS),
    accessoryType: pickIndex(4), // 0=none, 1=shield, 2=axe, 3=necklace
  };
}

/**
 * Generate a complete pixel-art character from parts.
 * Character size: 16x24 pixels.
 */
export function generateCharacterPixels(parts: CharacterParts): PixelData {
  const width = 16;
  const height = 24;
  const pixels: (string | null)[][] = Array.from({ length: height }, () =>
    Array.from({ length: width }, () => null),
  );

  const set = (x: number, y: number, color: string) => {
    if (x >= 0 && x < width && y >= 0 && y < height) {
      pixels[y]![x] = color;
    }
  };

  const fill = (x1: number, y1: number, x2: number, y2: number, color: string) => {
    for (let y = y1; y <= y2; y++) {
      for (let x = x1; x <= x2; x++) {
        set(x, y, color);
      }
    }
  };

  const helmetColor = HELMET_COLORS[parts.helmetType] ?? HELMET_COLORS[0]!;

  // ── Helmet (rows 0-5) ──
  if (parts.helmetType === 0) {
    // Basic rounded helmet
    fill(5, 1, 10, 1, helmetColor);
    fill(4, 2, 11, 4, helmetColor);
    fill(5, 5, 10, 5, helmetColor);
  } else if (parts.helmetType === 1) {
    // Horned helmet
    fill(5, 2, 10, 2, helmetColor);
    fill(4, 3, 11, 5, helmetColor);
    // Horns
    if (parts.hornType >= 1) {
      set(3, 2, '#c9a654');
      set(2, 1, '#c9a654');
      set(12, 2, '#c9a654');
      set(13, 1, '#c9a654');
    }
    if (parts.hornType >= 2) {
      set(1, 0, '#c9a654');
      set(14, 0, '#c9a654');
    }
  } else if (parts.helmetType === 2) {
    // Winged helmet
    fill(5, 2, 10, 2, helmetColor);
    fill(4, 3, 11, 5, helmetColor);
    // Wings
    set(3, 2, '#e0e0e0');
    set(2, 1, '#e0e0e0');
    set(1, 0, '#e0e0e0');
    set(12, 2, '#e0e0e0');
    set(13, 1, '#e0e0e0');
    set(14, 0, '#e0e0e0');
  } else {
    // Crown-style helmet
    fill(4, 3, 11, 5, helmetColor);
    set(5, 2, '#c9a654');
    set(7, 1, '#c9a654');
    set(8, 1, '#c9a654');
    set(10, 2, '#c9a654');
    set(7, 0, '#c9a654');
    set(8, 0, '#c9a654');
  }

  // Helmet rim
  fill(4, 5, 11, 5, '#3a3a3a');

  // ── Face (rows 6-10) ──
  fill(5, 6, 10, 10, parts.skinColor);

  // Eyes
  set(6, 7, '#1a1a2e');
  set(9, 7, '#1a1a2e');
  // Eye whites
  set(6, 7, '#ffffff');
  set(9, 7, '#ffffff');
  set(7, 7, '#1a1a2e');
  set(10, 7, '#1a1a2e');

  // Nose
  if (parts.noseType === 0) {
    set(8, 8, darken(parts.skinColor, 20));
  } else if (parts.noseType === 1) {
    set(7, 8, darken(parts.skinColor, 20));
    set(8, 8, darken(parts.skinColor, 20));
  } else {
    set(7, 8, darken(parts.skinColor, 20));
    set(8, 8, darken(parts.skinColor, 30));
    set(8, 9, darken(parts.skinColor, 15));
  }

  // Mouth
  set(7, 10, darken(parts.skinColor, 40));
  set(8, 10, darken(parts.skinColor, 40));

  // ── Beard (rows 10-13) ──
  if (parts.beardType >= 1) {
    // Short beard
    fill(5, 11, 10, 11, parts.hairColor);
  }
  if (parts.beardType >= 2) {
    // Medium beard
    fill(6, 12, 9, 12, parts.hairColor);
  }
  if (parts.beardType >= 3) {
    // Long beard
    fill(7, 13, 8, 14, parts.hairColor);
    set(6, 13, parts.hairColor);
    set(9, 13, parts.hairColor);
  }

  // ── Hair on sides ──
  set(4, 6, parts.hairColor);
  set(4, 7, parts.hairColor);
  set(11, 6, parts.hairColor);
  set(11, 7, parts.hairColor);

  // ── Body (rows 12-19) ──
  const bodyTop = parts.beardType >= 3 ? 15 : parts.beardType >= 2 ? 13 : 12;
  fill(4, bodyTop, 11, 19, parts.clothingColor);

  // Clothing details
  if (parts.clothingType === 0) {
    // Tunic - V neck
    set(7, bodyTop, parts.skinColor);
    set(8, bodyTop, parts.skinColor);
    set(7, bodyTop + 1, darken(parts.clothingColor, 20));
    set(8, bodyTop + 1, darken(parts.clothingColor, 20));
  } else if (parts.clothingType === 1) {
    // Armor - horizontal lines
    for (let y = bodyTop; y <= 19; y += 2) {
      fill(4, y, 11, y, darken(parts.clothingColor, 15));
    }
    // Belt
    fill(4, 17, 11, 17, '#8b6914');
    set(7, 17, '#c9a654');
    set(8, 17, '#c9a654');
  } else {
    // Fur trim
    fill(4, bodyTop, 11, bodyTop, '#b0a080');
    fill(4, bodyTop + 1, 11, bodyTop + 1, '#9a8a70');
  }

  // Arms
  fill(2, bodyTop + 1, 3, 18, parts.skinColor);
  fill(12, bodyTop + 1, 13, 18, parts.skinColor);
  // Sleeve
  fill(2, bodyTop + 1, 3, bodyTop + 2, parts.clothingColor);
  fill(12, bodyTop + 1, 13, bodyTop + 2, parts.clothingColor);

  // ── Legs (rows 20-23) ──
  fill(5, 20, 7, 22, '#4a3728');
  fill(8, 20, 10, 22, '#4a3728');
  // Boots
  fill(4, 23, 7, 23, '#3a2718');
  fill(8, 23, 11, 23, '#3a2718');

  // ── Accessory ──
  if (parts.accessoryType === 1) {
    // Shield on left arm
    fill(0, bodyTop + 2, 2, bodyTop + 5, '#8b4513');
    fill(1, bodyTop + 3, 1, bodyTop + 4, '#c9a654');
  } else if (parts.accessoryType === 2) {
    // Axe on right
    set(14, bodyTop, '#8b6914');
    set(14, bodyTop + 1, '#8b6914');
    set(14, bodyTop + 2, '#8b6914');
    set(15, bodyTop, '#a0a0a0');
    set(15, bodyTop + 1, '#a0a0a0');
  } else if (parts.accessoryType === 3) {
    // Necklace
    set(6, bodyTop, '#c9a654');
    set(7, bodyTop, '#c9a654');
    set(8, bodyTop, '#c9a654');
    set(9, bodyTop, '#c9a654');
    set(7, bodyTop + 1, '#ffcc00');
    set(8, bodyTop + 1, '#ffcc00');
  }

  return { width, height, pixels };
}

/**
 * Generate animation frames for idle animation.
 * Slight body sway (ping-pong).
 */
export function generateIdleFrames(parts: CharacterParts): PixelData[] {
  const base = generateCharacterPixels(parts);

  // Frame 1: base
  const frame1 = base;

  // Frame 2: shift upper body right by 1px
  const frame2 = shiftPixels(base, 0, -1, 0, 12);

  // Frame 3: base (same as frame1)
  const frame3 = clonePixelData(base);

  return [frame1, frame2, frame3];
}

// ═══════════════════════════════════════════════
// Helper functions
// ═══════════════════════════════════════════════

function darken(hex: string, amount: number): string {
  const r = Math.max(0, parseInt(hex.slice(1, 3), 16) - amount);
  const g = Math.max(0, parseInt(hex.slice(3, 5), 16) - amount);
  const b = Math.max(0, parseInt(hex.slice(5, 7), 16) - amount);
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

function clonePixelData(data: PixelData): PixelData {
  return {
    width: data.width,
    height: data.height,
    pixels: data.pixels.map((row) => [...row]),
  };
}

function shiftPixels(
  data: PixelData,
  dx: number,
  dy: number,
  fromRow: number,
  toRow: number,
): PixelData {
  const result = clonePixelData(data);

  // Clear affected rows first
  for (let y = fromRow; y <= Math.min(toRow, result.height - 1); y++) {
    for (let x = 0; x < result.width; x++) {
      result.pixels[y]![x] = null;
    }
  }

  // Copy shifted pixels
  for (let y = fromRow; y <= Math.min(toRow, data.height - 1); y++) {
    for (let x = 0; x < data.width; x++) {
      const color = data.pixels[y]?.[x];
      if (color) {
        const nx = x + dx;
        const ny = y + dy;
        if (nx >= 0 && nx < result.width && ny >= 0 && ny < result.height) {
          result.pixels[ny]![nx] = color;
        }
      }
    }
  }

  return result;
}
